import {
  Badge,
  Card,
  Grid,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useParams } from 'wouter';
import Link from '@/components/Link';
import { useAccountDetails } from '@/models/accounts';
import { usePayers } from '@/models/payers';

export default function AccountDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { data, isLoading, error } = useAccountDetails(id);
  const { data: payers = [] } = usePayers(id);

  if (isLoading || !data) {
    return error ? <Text c="red">Failed to load account.</Text> : null;
  }

  const { account, contacts, managers } = data;

  return (
    <Paper p="md" h="100%">
      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Card withBorder>
              <Stack gap={4}>
                <Group justify="space-between" align="flex-start">
                  <Title order={3}>{account.name}</Title>
                </Group>
                {account.notes && (
                  <Text size="sm" c="dimmed" mt={6}>
                    {account.notes}
                  </Text>
                )}
              </Stack>
            </Card>

            <Paper withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Title order={5}>Contacts</Title>
                <Text size="xs" c="dimmed">
                  {contacts.length}
                </Text>
              </Group>
              {contacts.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No contacts.
                </Text>
              ) : (
                <Table withRowBorders={false} verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Phone</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {contacts.map((c) => {
                      const name =
                        [c.firstName, c.lastName].filter(Boolean).join(' ') ||
                        `#${c.id}`;
                      return (
                        <Table.Tr key={c.id}>
                          <Table.Td>
                            <Link to={`~/contacts/${c.id}`}>{name}</Link>
                          </Table.Td>
                          <Table.Td>{c.email ?? '—'}</Table.Td>
                          <Table.Td>{c.phone ?? '—'}</Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>

            <Paper withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Title order={5}>Payers</Title>
                <Link to={`~/payers?accountId=${id}`}>Manage</Link>
              </Group>
              {payers.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No payers.
                </Text>
              ) : (
                <Table withRowBorders={false} verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>KYC</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {payers.map((p) => (
                      <Table.Tr key={p.id}>
                        <Table.Td>
                          <Link to={`~/payers/${p.id}`}>{p.displayName}</Link>
                        </Table.Td>
                        <Table.Td>{p.clientType}</Table.Td>
                        <Table.Td>
                          <Badge color={p.kycValid ? 'green' : 'red'} size="sm">
                            {p.kycValid ? 'Valid' : 'Expired'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Paper withBorder p="md">
              <Title order={6} mb={4}>
                Account managers
              </Title>
              <Text size="xs" c="dimmed" mb="xs">
                Derived from owners of contacts under this account.
              </Text>
              {managers.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No managers.
                </Text>
              ) : (
                <Stack gap={4}>
                  {managers.map((m) => (
                    <Text key={m.id} size="sm">
                      {m.name || m.email}
                    </Text>
                  ))}
                </Stack>
              )}
            </Paper>

            <Paper withBorder p="md">
              <Text size="sm" c="dimmed">
                Created {new Date(account.createdAt).toLocaleString()}
                {account.createdByUser && (
                  <>
                    {' '}
                    by{' '}
                    {account.createdByUser.name || account.createdByUser.email}
                  </>
                )}
              </Text>
              <Text size="sm" c="dimmed">
                Updated {new Date(account.updatedAt).toLocaleString()}
              </Text>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </Paper>
  );
}
